import { supabase } from '../lib/supabase';
import { Json } from '../types/supabase';
import { getChatbotPublicId } from './getChatbotPublicId';

// Types de métriques
type MetricName = 
  | 'response_time'
  | 'error_rate' 
  | 'api_calls'
  | 'memory_usage'
  | 'active_users'
  | 'conversation_length'
  | 'message_count'
  | 'lead_conversion_rate';

type MetricValue = number;

// Add type for Performance.memory which isn't in the standard DOM lib
interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

declare global {
  interface Performance {
    memory?: MemoryInfo;
  }
}

type MetricTags = {
  chatbot_public_id?: string;
  user_id?: string;  // Keep for backward compatibility but prefer chatbot_public_id
  endpoint?: string;
  status?: string;
  error_type?: string;
  feature?: string;
};

// DB record structure
interface MetricRecord {
  id?: string;
  name: string;
  value: number;
  tags: Json;
  timestamp: string;
  chatbot_public_id: string;  // Make required for DB insert
  created_at?: string | null;
}

// Classe principale de monitoring
export class Monitoring {
  private static instance: Monitoring;
  private metricsBuffer: MetricRecord[] = [];
  private readonly BUFFER_SIZE = 50;
  private readonly FLUSH_INTERVAL = 10000; // 10 secondes
  private defaultChatbotPublicId: string | null = null;

  private constructor() {
    // Démarrer le flush automatique
    setInterval(() => this.flush(), this.FLUSH_INTERVAL);

    // Écouter les événements de déchargement de la page
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  public static getInstance(): Monitoring {
    if (!Monitoring.instance) {
      Monitoring.instance = new Monitoring();
    }
    return Monitoring.instance;
  }

  // Set the default chatbotPublicId to use for all metrics
  public setDefaultChatbotPublicId(chatbotPublicId: string): void {
    this.defaultChatbotPublicId = chatbotPublicId;
  }

  // Get the current chatbotPublicId or try to get it from the context if not provided
  private async getChatbotPublicId(providedId?: string): Promise<string | undefined> {
    if (providedId) return providedId;
    if (this.defaultChatbotPublicId) return this.defaultChatbotPublicId;
    
    // Try to get from context as a fallback
    try {
      return await getChatbotPublicId();
    } catch (err) {
      console.warn('Could not get chatbotPublicId for metrics:', err);
      return undefined;
    }
  }

  // Enregistrer une métrique
  public async recordMetric(
    name: MetricName,
    value: MetricValue,
    tags: MetricTags = {}
  ): Promise<void> {
    // Ensure chatbot_public_id is set if possible
    const chatbotPublicId = await this.getChatbotPublicId(tags.chatbot_public_id);
    
    if (!chatbotPublicId) {
      console.warn('No chatbot_public_id available for metrics');
      return;
    }

    this.metricsBuffer.push({
      name,
      value,
      tags: tags as unknown as Json,
      timestamp: new Date().toISOString(),
      chatbot_public_id: chatbotPublicId
    });

    if (this.metricsBuffer.length >= this.BUFFER_SIZE) {
      await this.flush();
    }
  }

  // Enregistrer le temps de réponse
  public async recordResponseTime(
    endpoint: string,
    timeMs: number,
    tags: Omit<MetricTags, 'endpoint'> = {}
  ): Promise<void> {
    await this.recordMetric('response_time', timeMs, {
      ...tags,
      endpoint
    });
  }

  // Enregistrer une erreur
  public async recordError(
    error: Error,
    tags: MetricTags = {}
  ): Promise<void> {
    await this.recordMetric('error_rate', 1, {
      ...tags,
      error_type: error.name
    });
  }

  // Enregistrer l'utilisation de la mémoire
  public async recordMemoryUsage(chatbotPublicId?: string): Promise<void> {
    if (typeof performance !== 'undefined' && performance.memory) {
      const memoryInfo = performance.memory;
      await this.recordMetric('memory_usage', memoryInfo.usedJSHeapSize, {
        chatbot_public_id: await this.getChatbotPublicId(chatbotPublicId)
      });
    }
  }

  // Vider le buffer de métriques
  private async flush(): Promise<void> {
    if (this.metricsBuffer.length === 0) return;

    const metrics = [...this.metricsBuffer];
    this.metricsBuffer = [];

    try {
      // Filter out metrics without a valid chatbot_public_id
      const validMetrics = metrics.filter(m => 
        m.chatbot_public_id && m.chatbot_public_id !== 'unknown'
      );

      if (validMetrics.length === 0) {
        console.warn('No metrics with valid chatbot_public_id to flush');
        return;
      }

      const { error } = await supabase
        .from('metrics')
        .insert(validMetrics);

      if (error) throw error;
    } catch (err) {
      console.error('Error flushing metrics:', err);
      // Remettre les métriques dans le buffer en cas d'échec
      this.metricsBuffer = [...metrics, ...this.metricsBuffer];
    }
  }
}

// Convenience function to get the singleton instance
const getMonitoring = (): Monitoring => Monitoring.getInstance();

// Initialize monitoring with chatbotPublicId from the current context
export const initializeMonitoring = async (): Promise<void> => {
  try {
    const chatbotPublicId = await getChatbotPublicId();
    if (chatbotPublicId) {
      getMonitoring().setDefaultChatbotPublicId(chatbotPublicId);
    }
  } catch (err) {
    console.warn('Failed to initialize monitoring with chatbotPublicId:', err);
  }
};